/**
 * 注册页面 - 中式典雅风格
 * 配色：宣纸白 (#F8F6F1)、茶褐 (#7D6B55)、竹青 (#6B8E7C)、朱砂红 (#C44536)
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Card,
  Typography,
  Divider,
  message,
  Space,
  Row,
  Col,
} from 'antd';
import {
  UserOutlined,
  MailOutlined,
  LockOutlined,
  SafetyOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useStore } from '../../contexts/StoreContext';
import { authApi } from '../../api/index';

const { Title, Text } = Typography;

// 中式配色
const COLORS = {
  paper: '#F8F6F1',
  teaBrown: '#7D6B55',
  bambooGreen: '#6B8E7C',
  cinnabar: '#C44536',
  ink: '#2C2C2C',
  lightInk: '#5A5A5A',
  lightBorder: '#E0D8D0',
};

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [captchaSessionId, setCaptchaSessionId] = useState('');
  const [captchaImage, setCaptchaImage] = useState('');
  const [captchaLoading, setCaptchaLoading] = useState(false);

  const generateSessionId = () => {
    return 'sess_' + Date.now() + '_' + Math.random().toString(36).substring(2, 15);
  };

  const loadCaptchaImage = async () => {
    const sessionId = generateSessionId();
    setCaptchaSessionId(sessionId);
    setCaptchaLoading(true);
    try {
      const result = await authApi.getCaptchaImage(sessionId);
      setCaptchaImage(result.imageBase64);
    } catch {
      message.error('加载验证码失败');
    } finally {
      setCaptchaLoading(false);
    }
  };

  useEffect(() => {
    loadCaptchaImage();
  }, []);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      await register({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        password: values.password,
        captchaSessionId,
        captcha: values.captcha,
      });
      message.success('注册成功！');
      navigate('/login');
    } catch (err) {
      const errorMsg = err?.response?.data?.message || '注册失败，请重试';
      if (errorMsg.includes('验证码')) {
        loadCaptchaImage();
        form.setFieldsValue({ captcha: undefined });
      }
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${COLORS.paper} 0%, ${COLORS.teaBrown}08 100%)`,
        padding: '24px',
      }}
    >
      <div style={{ width: '100%', maxWidth: 440 }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <Title
            level={2}
            style={{
              color: COLORS.ink,
              margin: 0,
              letterSpacing: '0.3em',
              fontWeight: 300,
              fontSize: 26,
            }}
          >
            创建账户
          </Title>
          <div style={{
            width: 40,
            height: 2,
            background: COLORS.cinnabar,
            margin: '16px auto 0',
          }} />
        </div>

        <Card
          style={{
            borderRadius: 12,
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            border: 'none',
          }}
          bodyStyle={{ padding: '32px 32px 24px', background: COLORS.paper }}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            size="large"
          >
            <Row gutter={16}>
              <Col xs={12}>
                <Form.Item
                  name="firstName"
                  label={<span style={{ color: COLORS.ink, fontWeight: 500 }}>名字</span>}
                  rules={[{ required: true, message: '请输入名字' }]}
                >
                  <Input
                    prefix={<UserOutlined style={{ color: COLORS.bambooGreen }} />}
                    placeholder="名字"
                    style={{ borderRadius: 8 }}
                  />
                </Form.Item>
              </Col>
              <Col xs={12}>
                <Form.Item
                  name="lastName"
                  label={<span style={{ color: COLORS.ink, fontWeight: 500 }}>姓氏</span>}
                  rules={[{ required: true, message: '请输入姓氏' }]}
                >
                  <Input
                    prefix={<UserOutlined style={{ color: COLORS.bambooGreen }} />}
                    placeholder="姓氏"
                    style={{ borderRadius: 8 }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="email"
              label={<span style={{ color: COLORS.ink, fontWeight: 500 }}>邮箱</span>}
              rules={[
                { required: true, message: '请输入邮箱' },
                { type: 'email', message: '请输入有效的邮箱地址' },
              ]}
            >
              <Input
                prefix={<MailOutlined style={{ color: COLORS.bambooGreen }} />}
                placeholder="请输入邮箱"
                autoComplete="email"
                style={{ borderRadius: 8 }}
              />
            </Form.Item>

            <Form.Item
              name="password"
              label={<span style={{ color: COLORS.ink, fontWeight: 500 }}>密码</span>}
              rules={[
                { required: true, message: '请输入密码' },
                { min: 6, message: '密码至少 6 位' },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: COLORS.bambooGreen }} />}
                placeholder="请输入密码"
                autoComplete="new-password"
                style={{ borderRadius: 8 }}
              />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              label={<span style={{ color: COLORS.ink, fontWeight: 500 }}>确认密码</span>}
              dependencies={['password']}
              rules={[
                { required: true, message: '请确认密码' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('两次输入的密码不一致'));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: COLORS.bambooGreen }} />}
                placeholder="请再次输入密码"
                autoComplete="new-password"
                style={{ borderRadius: 8 }}
              />
            </Form.Item>

            <Form.Item
              name="captcha"
              label={<span style={{ color: COLORS.ink, fontWeight: 500 }}>验证码</span>}
              rules={[
                { required: true, message: '请输入验证码' },
                { len: 4, message: '验证码为 4 位' },
              ]}
            >
              <Row gutter={8}>
                <Col span={12}>
                  <Input
                    prefix={<SafetyOutlined style={{ color: COLORS.bambooGreen }} />}
                    placeholder="输入验证码"
                    maxLength={10}
                    autoComplete="off"
                    style={{ borderRadius: 8 }}
                  />
                </Col>
                <Col span={12}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: '40px' }}>
                    {captchaImage && (
                      <img
                        src={`data:image/png;base64,${captchaImage}`}
                        alt="验证码"
                        style={{
                          height: 40,
                          borderRadius: 4,
                          cursor: 'pointer',
                          border: `1px solid ${COLORS.lightBorder}`,
                        }}
                        onClick={loadCaptchaImage}
                        title="点击刷新验证码"
                      />
                    )}
                    <Button
                      icon={<ReloadOutlined style={{ color: COLORS.teaBrown }} />}
                      onClick={loadCaptchaImage}
                      loading={captchaLoading}
                      style={{ height: 40, borderRadius: 8, borderColor: COLORS.lightBorder }}
                    />
                  </div>
                </Col>
              </Row>
            </Form.Item>

            <Form.Item style={{ marginBottom: 12, marginTop: 8 }}>
              <Button
                type="primary"
                htmlType="submit"
                block
                loading={loading}
                style={{
                  height: 48,
                  fontSize: 16,
                  borderRadius: 8,
                  background: COLORS.teaBrown,
                  borderColor: COLORS.teaBrown,
                }}
              >
                注册
              </Button>
            </Form.Item>
          </Form>

          <Divider plain style={{ borderColor: `${COLORS.teaBrown}15` }}>
            <Text style={{ color: COLORS.lightInk, fontSize: 13 }}>或者</Text>
          </Divider>

          <div style={{ textAlign: 'center' }}>
            <Space>
              <Text style={{ color: COLORS.lightInk, fontSize: 13 }}>已有账户？</Text>
              <Link to="/login">
                <Text style={{ color: COLORS.cinnabar, fontWeight: 500, fontSize: 13 }}>立即登录</Text>
              </Link>
            </Space>
          </div>
        </Card>

        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <Link to="/">
            <Text style={{ color: COLORS.lightInk, fontSize: 13 }}>← 返回首页</Text>
          </Link>
        </div>
      </div>
    </div>
  );
}
